/**
 * The tracking engine: the pose model and the hand model, and the crops that
 * feed them. It runs inside public/js/track-worker.js, off the page's thread,
 * and on the page itself only when a worker cannot run the models (an older
 * Safari, a browser without WebGL in workers). Same code either way.
 *
 * process(src, t, opts) takes one camera frame and returns every landmark in
 * whole frame units (0..1), whatever crop the models were shown:
 *
 *   src     the frame: an ImageBitmap, or the <video> itself
 *   t       its time in ms, rising, which the pose model's tracking needs
 *   opts.W, opts.H   the camera frame's size in pixels
 *   opts.box   { x, y, w, h } in 0..1: show the pose model only this box
 *              (public/js/lockcrop.js asks for it when the player is small)
 *   opts.pose / opts.hands   which models run on this frame
 *   opts.hint  the player's hips, 0..1, to know whose wrists to crop around
 *   opts.looks sample shirt colours when more than one body is in frame
 *
 * Movement only. No face is read, matched or stored anywhere in here.
 */
import { handBoxes, planHandCrops, clipRect, letterbox, fromRegion, assignHands, pickPose, distinctPoses, handsVerdict } from './trackmath.js';
import { sampleLooks } from './looks.js';

/**
 * The page (public/dist/app.js) and the worker (public/js/*.js) are separate
 * files, and a cache can hold one from before a deploy and one from after. The
 * page sends this number; a worker whose own copy says otherwise refuses to
 * start, and the page runs the models itself for that session. Raise it
 * whenever a message between the two changes shape.
 */
export const TRACK_PROTOCOL = 1;

export const POSE_URL = (m) => `https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_${m}/float16/1/pose_landmarker_${m}.task`;
export const HAND_URL = 'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';

const POSE_PX = 640;          // a pose crop is drawn this big on its long side (lockcrop's old CROP_PX)
const HAND_PX = 256;          // a hand crop is drawn this big; the palm model's input is 192

/**
 * Google serves the models with a one hour cache life, so most sessions were a
 * fresh download. The bytes are kept in the Cache API instead, keyed by model,
 * and handed to MediaPipe as a buffer. The page and the worker share the cache.
 */
export async function modelBytes(url) {
  let cache = null;
  try { cache = await caches.open('hb-models-v1'); } catch { /* private mode, or no Cache API */ }
  try {
    const hit = cache && await cache.match(url);
    if (hit) return new Uint8Array(await hit.arrayBuffer());
  } catch { /* fall through to the network */ }
  const res = await fetch(url);
  if (!res.ok) throw new Error(`model ${res.status}`);
  const buf = await res.arrayBuffer();
  try { if (cache) await cache.put(url, new Response(buf.slice(0), { headers: { 'content-type': 'application/octet-stream' } })); } catch { /* storage full is not fatal */ }
  return new Uint8Array(buf);
}

const clock = () => performance.now();
const sizeOf = (src) => [src.videoWidth || src.displayWidth || src.width || 0, src.videoHeight || src.displayHeight || src.height || 0];
const canvasOf = (w, h) => (typeof OffscreenCanvas === 'function' ? new OffscreenCanvas(w, h) : Object.assign(document.createElement('canvas'), { width: w, height: h }));
const same = (a, b) => Math.abs(a.x - b.x) < 1 && Math.abs(a.y - b.y) < 1 && Math.abs(a.w - b.w) < 1 && Math.abs(a.h - b.h) < 1;

/**
 * A square canvas of side S holding `rect` (camera pixels) of the frame,
 * centred, letterboxed in black. Returns the canvas and the camera pixel square
 * the whole canvas stands for, so landmarks map back with fromRegion.
 */
function square(S) {
  const c = canvasOf(S, S);
  const g = c.getContext('2d', { alpha: false });
  return (src, rect, frameRect, srcW, srcH) => {
    const { dx, dy, dw, dh, region } = letterbox(rect, S);
    // Camera pixels to the pixels of `src`, which may be a picture of only part of the frame.
    const fx = srcW / frameRect.w, fy = srcH / frameRect.h;
    g.fillStyle = '#000'; g.fillRect(0, 0, S, S);
    g.drawImage(src, (rect.x - frameRect.x) * fx, (rect.y - frameRect.y) * fy, rect.w * fx, rect.h * fy, dx, dy, dw, dh);
    return { input: c, region };
  };
}

/**
 * When the hand models are built. The pose is what the player and the shot
 * need, so the engine is handed back as soon as the pose model is built, and
 * the first frames run on the pose alone. After AFTER pose frames (the first,
 * which compiles the GPU's shaders, not counted) the hand models are built,
 * while frames go on, unless the pose's median time on those frames is over
 * MAX_POSE_MS. A device that slow cannot afford a second model: with software
 * graphics (Chrome's SwiftShader, 2026-09-25) the pose took 1 to 3.5 seconds a
 * frame, and building the hand models beside it stopped the frames for more
 * than ten seconds. There the hands are left out and the pose keeps what speed
 * it has. With a real GPU (a Mac, same day) the pose took 13 to 14 ms a frame.
 */
export const HANDS_BUILD = { AFTER: 5, MAX_POSE_MS: 250 };

/**
 * Build the models. `vision` is the MediaPipe tasks-vision module and
 * `fileset` its wasm location, from FilesetResolver. `onStage('fetched')` is
 * called once the model bytes are in, before the models are built on the GPU,
 * `onStage('handsBuild')` when the hand models start building, and
 * `onStage('hands', hasHands)` once it is settled whether there are hands:
 * built, failed, or left out (HANDS_BUILD). `handsAfter: 0` builds them at
 * once (scripts/hands-eval.mjs); `handsBuilt` settles with the same answer.
 */
export async function createEngine({ vision, fileset, model = 'full', numPoses = 4, hands = true, handsAfter = HANDS_BUILD.AFTER, delegate = 'GPU', onStage = () => {} }) {
  const [poseBuf, handBuf] = await Promise.all([
    modelBytes(POSE_URL(model)),
    hands ? modelBytes(HAND_URL).catch(() => null) : Promise.resolve(null),
  ]);
  onStage('fetched');
  const pose = await vision.PoseLandmarker.createFromOptions(fileset, {
    // Full, not lite. Lite expects a body that fills the frame and it lost a
    // player thirty feet back on 2026-09-22.
    baseOptions: { modelAssetBuffer: poseBuf, delegate },
    runningMode: 'VIDEO', numPoses,
  });
  // Two hand models from the same bytes. The squares around the wrists go to
  // one in IMAGE mode, because every call is shown a different square: its
  // frame-to-frame tracking would follow a hand across squares that moved (the
  // square itself comes from the pose, which is tracked). When the whole
  // picture has to be searched instead (no body in view, or hands as big as
  // the frame), the other one searches it in VIDEO mode, following each hand
  // from one frame to the next as the app always did: it keeps a hand wrapped
  // round the ball that a fresh search misses. On the close up release clip
  // that found 37 hands where searching each frame afresh found 23
  // (scripts/hands-eval.mjs, 2026-09-25).
  // Tried on 2026-10-04 and dropped: one VIDEO mode model per wrist square. Offline it found as many
  // hands for 4 ms less a frame, but in the live app it crashed MediaPipe on the GPU within seconds
  // ("The tensor can't fit into OpenGL Texture2D View", then Aborted), which takes the tracking down.
  const handOpts = (runningMode) => ({
    baseOptions: { modelAssetBuffer: handBuf, delegate }, runningMode, numHands: 2,
    minHandDetectionConfidence: 0.4, minHandPresenceConfidence: 0.4,
  });
  const noHands = (e) => { console.warn('hand model unavailable', e?.message || e); return null; };
  let hand = null, handFull = null, closed = false;
  // 'waiting' for pose timings, 'building', or settled: 'ready' | 'none' | 'skipped'.
  let handsState = handBuf ? 'waiting' : 'none';
  let settle;
  const handsBuilt = new Promise((r) => { settle = r; });
  const settled = (ok) => { onStage('hands', ok); settle(ok); };
  function buildHands() {
    if (handsState !== 'waiting') return;
    handsState = 'building';
    onStage('handsBuild');
    Promise.all([
      vision.HandLandmarker.createFromOptions(fileset, handOpts('IMAGE')).catch(noHands),
      vision.HandLandmarker.createFromOptions(fileset, handOpts('VIDEO')).catch(noHands),
    ]).then(([a, b]) => {
      // Closed while they were being built: they are not wanted any more.
      if (closed) { try { a?.close(); } catch {} try { b?.close(); } catch {} return; }
      hand = a; handFull = b;
      handsState = hand || handFull ? 'ready' : 'none';
      settled(handsState === 'ready');
    });
  }
  const poseTimes = [];
  /** One pose frame's time: once there are enough, build the hands or leave them out. */
  function notePose(ms) {
    if (handsState !== 'waiting' || !Number.isFinite(ms)) return;
    poseTimes.push(ms);
    const v = handsVerdict(poseTimes, { after: handsAfter, maxPoseMs: HANDS_BUILD.MAX_POSE_MS });
    if (v === 'skip') { handsState = 'skipped'; settled(false); }
    else if (v === 'build') buildHands();
  }
  if (!handBuf) settled(false);
  else if (handsAfter <= 0) buildHands();

  const poseCrop = square(POSE_PX), handCrop = square(HAND_PX);
  let lastPoses = [];

  return {
    model, delegate,
    /** True once a hand model is built and running. */
    get hasHands() { return Boolean(hand || handFull); },
    handsBuilt,
    close() { closed = true; try { pose.close(); } catch {} try { hand?.close(); } catch {} try { handFull?.close(); } catch {} },

    process(src, t, o = {}) {
      const tStart = clock();
      const [sw, sh] = sizeOf(src);
      const W = o.W || sw, H = o.H || sh;
      // What `src` shows of the camera frame. The whole frame unless the page
      // sent a cut of it.
      const rect = o.rect || { x: 0, y: 0, w: W, h: H };
      const out = { t, poses: null, worlds: null, cropped: false, lost: false, hands: null, looks: null, ms: {} };

      if (o.pose !== false) {
        const t0 = clock();
        let input = src, region = rect;
        if (o.box) {
          const want = clipRect({ x: o.box.x * W, y: o.box.y * H, w: o.box.w * W, h: o.box.h * H }, rect);
          if (want && !same(want, rect)) ({ input, region } = poseCrop(src, want, rect, sw, sh));
          out.cropped = true;
        }
        out.ms.poseCrop = clock() - t0;
        const r = pose.detectForVideo(input, t);
        const all = (r.landmarks || []).map((lm) => fromRegion(lm, region, W, H));
        // The same person twice is one person (trackmath.js distinctPoses).
        const keep = distinctPoses(all, W, H);
        out.poses = keep.map((i) => all[i]);
        out.worlds = keep.map((i) => r.worldLandmarks?.[i] || null);
        out.copies = all.length - keep.length;
        if (out.cropped && !out.poses.length) out.lost = true;
        lastPoses = out.poses;
        out.ms.pose = clock() - t0;
        notePose(out.ms.pose);
      }

      if (o.hands && (hand || handFull)) {
        const t0 = clock();
        const player = pickPose(out.poses || lastPoses, o.hint);
        let crops = player ? planHandCrops(handBoxes(player, W, H), W, H) : [];
        // No body, or none of its wrists in sight: hands held up to the camera
        // are still hands, so the whole picture is searched.
        if (!crops.length) crops = [{ x: 0, y: 0, w: W, h: H, sides: [], full: true }];
        const found = [];
        let drawMs = 0;
        for (const c of crops) {
          const want = clipRect(c, rect);
          if (!want) continue;
          const whole = same(want, rect);
          const td = clock();
          const shown = whole ? { input: src, region: rect } : handCrop(src, want, rect, sw, sh);
          drawMs += clock() - td;
          const r = whole && handFull ? handFull.detectForVideo(shown.input, t) : hand ? hand.detect(shown.input) : null;
          (r?.landmarks || []).forEach((lm, i) => found.push({ lm: fromRegion(lm, shown.region, W, H), score: r.handedness?.[i]?.[0]?.score ?? null }));
        }
        const wrists = player ? {
          left: (player[15]?.visibility ?? 1) >= 0.3 ? player[15] : null,
          right: (player[16]?.visibility ?? 1) >= 0.3 ? player[16] : null,
        } : null;
        out.hands = assignHands(found, wrists, W, H);
        out.handCrops = crops.length;
        out.ms.handDraw = drawMs;
        out.ms.hands = clock() - t0;
      }

      // Shirt colours for the tracker, only when there is somebody to confuse
      // the player with, and only from a picture of the whole frame.
      if (o.looks && out.poses && out.poses.length > 1 && same(rect, { x: 0, y: 0, w: W, h: H })) {
        const t0 = clock();
        out.looks = sampleLooks(src, out.poses);
        out.ms.looks = clock() - t0;
      }
      out.ms.total = clock() - tStart;
      return out;
    },
  };
}
